import React from 'react';
import {IntlShape, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';

import {FlexRow, Text} from '@forecast-it/design-system';

import Util from '../../../../../util/util';
import {ESTIMATION_UNIT} from '../../../../../../../constants';

import type {TimeRegistrationModalTaskInfo_task} from './__generated__/TimeRegistrationModalTaskInfo_task.graphql';

type FormatTimeInput = {
	time: number;
	estimationUnit?: string | null;
	intl: IntlShape;
};
const formatTime = ({time, estimationUnit, intl}: FormatTimeInput): string => {
	if (estimationUnit === ESTIMATION_UNIT.POINTS) {
		return Util.roundPoints(time, intl);
	}
	return Util.convertMinutesToFullHour(time, intl);
};

type ConvertStoryPointsToMinutesInput = {
	points: number;
	minutesPerEstimationPoint: number;
};
const convertStoryPointsToMinutes = ({points, minutesPerEstimationPoint}: ConvertStoryPointsToMinutesInput): number => {
	if (minutesPerEstimationPoint > 0) {
		return points / minutesPerEstimationPoint;
	}
	return 0;
};

type ConvertStoryPointsOrMinutesToMinutesInput = {
	pointsOrMinutes: number;
	estimationUnit?: string | null;
	minutesPerEstimationPoint?: number | null;
};
const convertStoryPointsOrMinutesToMinutes = ({
	pointsOrMinutes,
	minutesPerEstimationPoint,
	estimationUnit,
}: ConvertStoryPointsOrMinutesToMinutesInput): number => {
	if (estimationUnit === ESTIMATION_UNIT.POINTS && minutesPerEstimationPoint) {
		return convertStoryPointsToMinutes({points: pointsOrMinutes, minutesPerEstimationPoint});
	}
	return pointsOrMinutes;
};

/* when editing a time registration, the time left on the task should include the time initially registered
 *  editing a 4 hour timereg on an 8 hour task should show 8 hours remaining, not 4.
 * */
function getTimeRemainingIncludingInitialTime(
	estimateForecastMinutes: number | null,
	totalMinutesRegistered: number | null,
	initialTime?: number,
	addInitialTimeToRemaining?: boolean
): number {
	if (!estimateForecastMinutes) {
		return 0;
	}

	const time = (!!addInitialTimeToRemaining && initialTime) || 0;

	return estimateForecastMinutes - (totalMinutesRegistered || 0) + time;
}

type TimeRegistrationModalTaskInfoProps = {
	task: TimeRegistrationModalTaskInfo_task;
	initialTime?: number;
	addInitialTimeToRemaining?: boolean;
};

function TimeRegistrationModalTaskInfo({
	task,
	initialTime,
	addInitialTimeToRemaining,
}: TimeRegistrationModalTaskInfoProps): JSX.Element {
	const intl = useIntl();
	const {estimateForecastMinutes, totalMinutesRegistered} = task;

	const convertedRemaining = convertStoryPointsOrMinutesToMinutes({
		pointsOrMinutes: getTimeRemainingIncludingInitialTime(
			estimateForecastMinutes,
			totalMinutesRegistered,
			initialTime,
			addInitialTimeToRemaining
		),
		minutesPerEstimationPoint: task.project?.minutesPerEstimationPoint,
		estimationUnit: task.project?.estimationUnit,
	});
	const convertedEstimate = convertStoryPointsOrMinutesToMinutes({
		pointsOrMinutes: estimateForecastMinutes ?? 0,
		minutesPerEstimationPoint: task.project?.minutesPerEstimationPoint,
		estimationUnit: task.project?.estimationUnit,
	});

	const formattedTaskEstimate = formatTime({
		time: convertedEstimate,
		estimationUnit: task.project?.estimationUnit,
		intl: intl,
	});
	const formattedTaskRemaining = formatTime({
		time: convertedRemaining,
		estimationUnit: task.project?.estimationUnit,
		intl: intl,
	});

	return (
		<FlexRow>
			<Text color="medium" noWrap>{`${formattedTaskRemaining} remaining on ${formattedTaskEstimate} task`}</Text>
		</FlexRow>
	);
}

export default createFragmentContainer(TimeRegistrationModalTaskInfo, {
	task: graphql`
		fragment TimeRegistrationModalTaskInfo_task on Task {
			id
			name
			estimateForecastMinutes
			totalMinutesRegistered
			project {
				estimationUnit
				minutesPerEstimationPoint
			}
		}
	`,
});
